import React, { useState } from 'react';
import { Modal, Button, Badge, Table, Row, Col } from 'react-bootstrap';
import {
    FaCheckCircle, FaArrowRight, FaTrash, FaPlus, FaSave,
    FaEye, FaExchangeAlt, FaUserTie, FaBuilding, FaFileInvoiceDollar
} from 'react-icons/fa';
import { formatCurrency } from '../../../utility';

import styled from 'styled-components';
import SerialInspector from './SerialInspector';

// --- STYLED COMPONENTS ---
const StyledReviewModal = styled(Modal)`
  .modal-content {
    border: none;
    border-radius: 20px;
    box-shadow: 0 40px 80px -12px rgba(0, 0, 0, 0.15);
    background-color: #f8fafc;
  }
  .modal-header {
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    padding: 1.5rem 2rem;
    border-radius: 20px 20px 0 0;
  }
  .modal-footer {
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    padding: 1.5rem 2rem;
    border-radius: 0 0 20px 20px;
  }
`;

const SummaryCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
`;

const DiffBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${props => props.bg || '#f1f5f9'};
  color: ${props => props.color || '#475569'};
`;

const PurchaseReview = ({
    show,
    onHide,
    onConfirm,
    changes,
    values,
    isSubmitting,
    isEditMode,
    isDirect,
    supplierData,
    customerData,
    agentData
}) => {
    const [selectedSerials, setSelectedSerials] = useState(null);
    const { added = [], modified = [], deleted = [] } = changes;

    // Logic setup
    const isTradeIn = values.purchase_type === "Trade-In";
    const entityName = isTradeIn
        ? (customerData ? `${customerData.first_name} ${customerData.last_name}` : "Unknown Customer")
        : (supplierData ? supplierData.business_name : "Unknown Supplier");
    const entityId = isTradeIn ? customerData?.customer_id : supplierData?.supplier_id;
    const currentBalance = isTradeIn ? (customerData?.financial?.current_balance || 0) : (supplierData?.financial?.current_balance || 0);
    const newBalance = currentBalance + (values.grand_total || 0);

    const resolveItemName = (item) => {
        const variantName = item.variantName || item.variant_id?.variantName;
        if (variantName) return variantName;

        const attributes = item.variantAttributes || [];
        const attrString = attributes.length > 0
            ? ` (${attributes.map(a => a.value).filter(Boolean).join(' / ')})`
            : '';

        return `${item.itemName || 'Unknown Item'}${attrString}`;
    };

    return (
        <>
            <StyledReviewModal show={show} onHide={onHide} size="xl" centered scrollable backdrop="static">

                {/* 1. HEADER */}
                <Modal.Header>
                    <div className="d-flex align-items-center gap-3 w-100">
                        <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                            <FaFileInvoiceDollar size={24} />
                        </div>
                        <div>
                            <h4 className="fw-900 mb-0 text-dark" style={{ letterSpacing: '-0.5px' }}>Audit & Finalize</h4>
                            <div className="text-dark small fw-bold mt-1">
                                {isEditMode ? 'REVIEW PURCHASE MODIFICATIONS' : 'REVIEW NEW PURCHASE ENTRY'}
                            </div>
                        </div>
                        <div className="ms-auto text-end">
                            <Badge bg={isDirect ? "success-subtle" : "info-subtle"} text={isDirect ? "success" : "info"} className="border px-3 py-2 rounded-pill text-uppercase">
                                {isDirect ? "Direct Stock In" : "PO Completion"}
                            </Badge>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="px-4 py-4">

                    {/* 2. FINANCIAL SUMMARY GRID */}
                    <Row className="g-3 mb-5">
                        {/* Entity Info */}
                        <Col md={4}>
                            <SummaryCard>
                                <div className="text-dark small fw-bold uppercase mb-2">
                                    {isTradeIn ? 'Customer' : 'Supplier'} Details
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-dark text-white rounded-circle d-flex align-items-center justify-content-center fw-bold fs-5" style={{ width: '45px', height: '45px' }}>
                                        {entityName.charAt(0)}
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="fw-bold text-truncate text-dark">{entityName}</div>
                                        <div className="text-dark small font-monospace fw-bold">{entityId}</div>
                                    </div>
                                </div>
                                {agentData && (
                                    <div className="mt-3 pt-3 border-top d-flex align-items-center gap-2 text-muted x-small">
                                        <FaUserTie /> Agent: <span className="fw-bold">{agentData.name}</span>
                                    </div>
                                )}
                            </SummaryCard>
                        </Col>

                        {/* Liability Shift */}
                        <Col md={4}>
                            <SummaryCard>
                                <div className="text-dark small fw-bold uppercase mb-2">Account Balance Impact</div>
                                <div className="d-flex align-items-center justify-content-between mt-1">
                                    <div>
                                        <div className="text-muted x-small mb-1">Current</div>
                                        <div className="fw-bold text-dark fw-bold font-monospace fs-5">
                                            {formatCurrency(currentBalance)}
                                        </div>
                                    </div>
                                    <FaArrowRight className="text-muted opacity-50" />
                                    <div className="text-end">
                                        <div className="text-primary small fw-bold mb-1">Post-Transaction</div>
                                        <div className="fw-900 text-dark fs-5 font-monospace">
                                            {formatCurrency(newBalance)}
                                        </div>
                                    </div>
                                </div>
                            </SummaryCard>
                        </Col>

                        {/* Grand Total */}
                        <Col md={4}>
                            <SummaryCard style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                                <div className="text-success small fw-bold uppercase mb-2">Total Transaction Value</div>
                                <div className="d-flex align-items-end justify-content-between h-100">
                                    <div className="fw-900 text-success fs-2 font-monospace" style={{ lineHeight: 1 }}>
                                        {formatCurrency(values.grand_total)}
                                    </div>
                                    <Badge bg="success" className="mb-1 rounded-pill">+ Invoice</Badge>
                                </div>
                            </SummaryCard>
                        </Col>
                    </Row>


                    {/* 3. CHANGES: ADDED */}
                    {added.length > 0 && (
                        <div className="mb-5">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <DiffBadge bg="#dcfce7" color="#166534"><FaPlus /> ADDED</DiffBadge>
                                <span className="text-muted fw-bold small">New Inventory Injections</span>
                            </div>
                            <div className="bg-white rounded-4 border shadow-sm overflow-hidden">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr className="text-dark small text-uppercase fw-900">
                                            <th className="ps-4 py-3 border-bottom-0">Item Description</th>
                                            <th className="text-center border-bottom-0">Stock Type</th>
                                            <th className="text-end border-bottom-0">Qty</th>
                                            <th className="text-end border-bottom-0">Unit Cost</th>
                                            <th className="text-end pe-4 border-bottom-0">Line Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {added.map((item, i) => (
                                            <tr key={i}>
                                                <td className="ps-4">
                                                    <div className="fw-bold text-dark">{resolveItemName(item)}</div>
                                                    {item.isSerialized && (
                                                        <Button
                                                            variant="link" className="p-0 text-decoration-none small fw-bold mt-1 d-flex align-items-center gap-1"
                                                            onClick={() => setSelectedSerials({ itemName: item.itemName, serials: item.serializedItems || [] })}
                                                        >
                                                            <FaEye /> View {item.serializedItems?.length} Serials
                                                        </Button>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <Badge bg="light" text="dark" className="border fw-normal small fw-bold text-dark">
                                                        {item.isSerialized ? 'Serialized' : 'Bulk'}
                                                    </Badge>
                                                </td>
                                                <td className="text-end fw-bold text-success font-monospace">+{item.purchaseQty}</td>
                                                <td className="text-end font-monospace text-muted">{formatCurrency(item.unitCost)}</td>
                                                <td className="text-end pe-4 font-monospace fw-bold text-dark">{formatCurrency(item.total_price)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}


                    {/* 4. CHANGES: MODIFIED */}
                    {modified.length > 0 && (
                        <div className="mb-5">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <DiffBadge bg="#ffedd5" color="#9a3412"><FaExchangeAlt /> MODIFIED</DiffBadge>
                                <span className="text-muted fw-bold small">Adjustments to Existing Lines</span>
                            </div>
                            <div className="bg-white rounded-4 border shadow-sm overflow-hidden">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr className="text-dark small text-uppercase fw-900">
                                            <th className="ps-4 py-3 border-bottom-0">Item Description</th>
                                            <th className="text-center border-bottom-0">Qty Delta</th>
                                            <th className="text-center border-bottom-0">Cost Delta</th>
                                            <th className="text-end pe-4 border-bottom-0">Revised Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modified.map((item, i) => {
                                            const qtyDiff = item.after.qty - item.before.qty;
                                            const costDiff = item.after.cost - item.before.cost;
                                            return (
                                                <tr key={i}>
                                                    <td className="ps-4">
                                                        <div className="fw-bold text-dark">{resolveItemName(item)}</div>
                                                        <div className="text-dark small fw-bold">Stock: {item.currentStock} ➝ <span className="fw-bold text-dark">{item.currentStock + qtyDiff}</span></div>
                                                    </td>
                                                    <td className="text-center">
                                                        {qtyDiff !== 0 ? (
                                                            <Badge bg={qtyDiff > 0 ? "success-subtle" : "danger-subtle"} text={qtyDiff > 0 ? "success" : "danger"} className="rounded-pill font-monospace">
                                                                {qtyDiff > 0 ? '+' : ''}{qtyDiff} Units
                                                            </Badge>
                                                        ) : <span className="text-muted text-opacity-25">—</span>}
                                                    </td>
                                                    <td className="text-center">
                                                        {costDiff !== 0 ? (
                                                            <div className="d-flex flex-column align-items-center">
                                                                <span className="x-small text-muted text-decoration-line-through font-monospace">{formatCurrency(item.before.cost)}</span>
                                                                <span className="fw-bold text-dark font-monospace">{formatCurrency(item.after.cost)}</span>
                                                            </div>
                                                        ) : <span className="text-muted text-opacity-25">—</span>}
                                                    </td>
                                                    <td className="text-end pe-4 fw-bold font-monospace">{formatCurrency(item.after.qty * item.after.cost)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}


                    {/* 5. CHANGES: DELETED */}
                    {deleted.length > 0 && (
                        <div className="mb-5">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <DiffBadge bg="#fee2e2" color="#991b1b"><FaTrash /> REMOVED</DiffBadge>
                                <span className="text-muted fw-bold small">Items to be Voided</span>
                            </div>
                            <div className="bg-danger bg-opacity-10 rounded-4 border border-danger border-opacity-25 overflow-hidden">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="bg-danger bg-opacity-10">
                                        <tr className="text-danger x-small text-uppercase fw-bold">
                                            <th className="ps-4 py-3 border-bottom-0">Voided Item</th>
                                            <th className="text-center border-bottom-0">Qty Reverted</th>
                                            <th className="text-end pe-4 border-bottom-0">Credit Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deleted.map((item, i) => (
                                            <tr key={i}>
                                                <td className="ps-4 text-decoration-line-through text-muted fw-bold">{resolveItemName(item)}</td>
                                                <td className="text-center text-danger font-monospace fw-bold">-{item.purchaseQty}</td>
                                                <td className="text-end pe-4 text-muted font-monospace">{formatCurrency(item.total_price)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                </Modal.Body>

                {/* 6. FOOTER ACTIONS */}
                <Modal.Footer>
                    <div className="d-flex w-100 justify-content-between align-items-center">
                        <div className="text-dark small fw-bold">
                            <FaCheckCircle className="me-1" /> All system checks passed
                        </div>
                        <div className="d-flex gap-3">
                            <Button variant="light" className="fw-bold border text-muted px-4" onClick={onHide} disabled={isSubmitting}>
                                Cancel & Review
                            </Button>
                            <Button
                                variant="dark"
                                className="fw-bold px-5 d-flex align-items-center gap-2"
                                onClick={onConfirm}
                                disabled={isSubmitting}
                                style={{ background: '#0f172a', border: 'none' }}
                            >
                                {isSubmitting ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Processing...</>
                                ) : (
                                    <><FaSave /> Confirm & Finalize</>
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </StyledReviewModal>

            {/* SERIAL INSPECTOR SUB-MODAL */}
            {selectedSerials && (
                <SerialInspector
                    show={!!selectedSerials}
                    onHide={() => setSelectedSerials(null)}
                    itemName={selectedSerials.itemName}
                    serials={selectedSerials.serials || []}
                />
            )}
        </>
    );
};

export default PurchaseReview;